import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import { 
  ChevronDown, 
  ChevronUp, 
  ArrowRight, 
  CreditCard, 
  CalendarX, 
  ShieldCheck, 
  ClipboardCheck, 
  Check 
} from 'lucide-react';

export default function Priser() {
  const { lang, getLocalizedValue } = useLanguage();
  const [activeTab, setActiveTab] = useState('udredning');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  // CMS State
  const [pageData, setPageData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [cmsPrices, setCmsPrices] = useState([]);
  const [cmsFaq, setCmsFaq] = useState([]);

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Priser og ydelser | Nordisk Psykiatri' 
    : 'Priser och tjänster | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Se priser på udredning og behandling samt praktisk information om betaling, afbud og tilskud hos Nordisk Psykiatri.'
    : 'Se priser för utredning och behandling samt praktisk information om betalning, avbokning och bidrag hos Nordisk Psykiatri.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  useEffect(() => {
    // 1. Fetch Page Header & Section Config from priser_side
    const fetchPageData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/priser_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setPageData(data[0]);
          }
        }
      } catch (e) {
        console.error('Fejl ved hentning af priser_side:', e);
      }
    };

    // 2. Fetch Packages from priser_pakker
    const fetchPackages = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/priser_pakker');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const sorted = [...data].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            setPackages(sorted);
          }
        }
      } catch (e) {
        console.error('Fejl ved hentning af priser_pakker:', e);
      }
    };

    // 3. Fetch Single Pricing Items from priser
    const fetchPrices = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/priser');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const sorted = [...data].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            setCmsPrices(sorted);
          }
        }
      } catch (e) {
        console.error('Fejl ved hentning af priser:', e);
      }
    };

    // 4. Fetch Pricing FAQs from shared ofte_stillede_spoergsmaal (with priser_faq fallback)
    const fetchFaq = async () => {
      try {
        let res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:priser&limit=7&sort=sortering&order=asc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsFaq(data.items);
            return;
          }
        }
        // Fallback to priser_faq if needed
        res = await fetch('https://api.dev.auxo.dk/public/content/priser_faq');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const sorted = [...data].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            setCmsFaq(sorted);
          }
        }
      } catch (e) {
        console.error('Fejl ved hentning af priser FAQs:', e);
      }
    };

    fetchPageData();
    fetchPackages();
    fetchPrices();
    fetchFaq();

    if (window.location.hash === '#afbud-og-betaling' || window.location.hash === '#faq') {
      setOpenFaqIndex(0);
      setTimeout(() => {
        const element = document.getElementById('faq');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Helper to extract list items from HTML string
  const parseHtmlList = (html) => {
    if (!html) return [];
    if (typeof html !== 'string') return [];
    
    if (typeof document !== 'undefined') {
      try {
        const div = document.createElement('div');
        div.innerHTML = html;

        // 1. Look for list items (li) whether in ul, ol, or with attributes like <li value="1">
        const liElements = Array.from(div.querySelectorAll('li'));
        if (liElements.length > 0) {
          const items = liElements.map(el => el.textContent.trim()).filter(Boolean);
          if (items.length > 0) return items;
        }

        // 2. Look for paragraphs (p)
        const pElements = Array.from(div.querySelectorAll('p'));
        if (pElements.length > 0) {
          const items = pElements.map(el => el.textContent.trim()).filter(Boolean);
          if (items.length > 0) return items;
        }

        // 3. Fallback: replace <br> with newline and split
        const textWithBreaks = div.innerHTML
          .replace(/<br\s*[\/]?>/gi, '\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<[^>]+>/g, '');
        const lines = textWithBreaks.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length > 0) return lines;
      } catch (err) {
        console.error('Error parsing HTML list:', err);
      }
    }

    // Regex fallback
    const liMatches = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
    if (liMatches.length > 0) {
      return liMatches
        .map(m => m[1].replace(/<[^>]+>/g, '').trim())
        .filter(Boolean);
    }

    const pMatches = [...html.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)];
    if (pMatches.length > 0) {
      return pMatches
        .map(m => m[1].replace(/<[^>]+>/g, '').trim())
        .filter(Boolean);
    }

    return html
      .replace(/<br\s*[\/]?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .split('\n')
      .map(t => t.trim())
      .filter(Boolean);
  };

  // Fallback Udredning table items
  const fallbackUdredningItems = [
    { name: 'Klinisk interview og screening', duration: 'ca. 2 timer', price: '3.200 kr.' },
    { name: 'ADHD-udredning (DIVA-5)', duration: 'ca. 2 timer', price: '3.500 kr.' },
    { name: 'Autismeudredning (AAA)', duration: 'ca. 2 timer', price: '3.500 kr.' },
    { name: 'ADOS-2 (ved behov)', duration: 'ca. 2 timer', price: '3.500 kr.' },
    { name: 'Tilbagemelding med epikrise', duration: '1 time epikrise + 1 time tilbagemelding', price: '3.000 kr.' },
    { name: 'OCD-udredning', duration: 'ca. 2 timer', price: '3.500 kr.' },
    { name: 'PTSD-udredning', duration: 'ca. 2 timer', price: '3.500 kr.' },
    { name: 'Dokumentation', duration: 'pr. time', price: '1.250 kr.' },
  ];

  // Fallback Behandling table items
  const fallbackBehandlingItems = [
    { name: 'Psykiaterkonsultation (medicinsamtale)', duration: 'ca. 1 time', price: '2.400 kr.' },
    { name: 'Psykiateropfølgning (medicin)', duration: 'ca. 30 min.', price: '1.200 kr.' },
    { name: 'Psykologsamtale / terapi', duration: '60 min.', price: '1.300 kr.' },
    { name: 'Forlænget terapisession', duration: 'ca. 1,5 time', price: '1.900 kr.' },
    { name: 'Pårørendesamtale / parterapi', duration: '60 min.', price: '1.500 kr.' },
  ];

  // Filter dynamic CMS table items
  const cmsUdredning = cmsPrices.filter(p => 
    p.terms?.['pris-kategori']?.includes('udredning') || 
    p.terms?.['pris-kategori']?.includes('udredningsforloeb') || 
    p.terms?.['pris-kategori']?.includes('attester')
  );
  const cmsBehandling = cmsPrices.filter(p => 
    p.terms?.['pris-kategori']?.includes('behandling') || 
    p.terms?.['pris-kategori']?.includes('individuelle-konsultationer') || 
    p.terms?.['pris-kategori']?.includes('terapi')
  );

  const activeUdredningList = cmsUdredning.length > 0 ? cmsUdredning.map(item => ({
    name: getLocalizedValue(item.titel),
    duration: getLocalizedValue(item.varighed_eller_detaljer),
    price: getLocalizedValue(item.pris)
  })) : fallbackUdredningItems;

  const activeBehandlingList = cmsBehandling.length > 0 ? cmsBehandling.map(item => ({
    name: getLocalizedValue(item.titel),
    duration: getLocalizedValue(item.varighed_eller_detaljer),
    price: getLocalizedValue(item.pris)
  })) : fallbackBehandlingItems;

  // Fallback packages
  const fallbackCourses = [
    {
      title: 'ADHD-udredning',
      price: lang === 'da' ? '9.700 kr.' : '14.500 SEK',
      items: [
        'Klinisk indledende samtale og screening',
        'Psykiatrisk anamnese',
        'DIVA-5-interview',
        'Tværfaglig konference og samlet vurdering',
        'Mundtlig tilbagemelding',
        'Epikrise og individuel behandlingsplan'
      ]
    },
    {
      title: 'ADHD- og autismeudredning',
      price: lang === 'da' ? '13.200 kr.' : '19.800 SEK',
      items: [
        'Klinisk indledende samtale og screening',
        'Psykiatrisk anamnese',
        'DIVA-5-interview',
        'AAA-interview',
        'Tværfaglig konference og samlet vurdering',
        'Tilbagemelding, epikrise og behandlingsplan'
      ]
    },
    {
      title: 'ADHD-udredning + første medicinsamtale',
      price: lang === 'da' ? '12.100 kr.' : '18.200 SEK',
      items: [
        'Samlet ADHD-udredningsforløb',
        'Epikrise og individuel behandlingsplan',
        'Første medicinsamtale hos psykiater',
        'Receptudstedelse',
        'Plan for eventuel opstart af medicin'
      ]
    }
  ];

  const activePackages = packages.length > 0 ? packages.map(pkg => ({
    title: getLocalizedValue(pkg.titel),
    price: getLocalizedValue(pkg.pris),
    items: parseHtmlList(getLocalizedValue(pkg.inkluderede_punkter)),
    buttonText: getLocalizedValue(pkg.knap_tekst) || (lang === 'da' ? 'Kontakt os om booking' : 'Contact us regarding booking'),
    buttonLink: pkg.knap_link || '/kontakt'
  })) : fallbackCourses;

  // Practical information items
  const practicalPoints = [
    { 
      icon: CreditCard, 
      title: getLocalizedValue(pageData?.info_1_titel) || (lang === 'da' ? 'Betaling' : 'Payment'), 
      text: getLocalizedValue(pageData?.info_1_tekst) || (lang === 'da' ? 'Efter hver konsultation' : 'After each consultation') 
    },
    { 
      icon: CalendarX, 
      title: getLocalizedValue(pageData?.info_2_titel) || (lang === 'da' ? 'Afbud' : 'Cancellation'), 
      text: getLocalizedValue(pageData?.info_2_tekst) || (lang === 'da' ? 'Senest 48 timer før' : 'At least 48 hours in advance') 
    },
    { 
      icon: ShieldCheck, 
      title: getLocalizedValue(pageData?.info_3_titel) || (lang === 'da' ? 'Sygeforsikring Danmark' : 'Health Insurance Denmark'), 
      text: getLocalizedValue(pageData?.info_3_tekst) || (lang === 'da' ? 'Mulighed for tilskud' : 'Possibility of reimbursement') 
    },
    { 
      icon: ClipboardCheck, 
      title: getLocalizedValue(pageData?.info_4_titel) || (lang === 'da' ? 'Henvisning' : 'Referral'), 
      text: getLocalizedValue(pageData?.info_4_tekst) || (lang === 'da' ? 'Ikke nødvendigt' : 'Not required') 
    }
  ];

  // Fallback FAQ
  const fallbackFaqItems = [
    {
      q: '1. Hvad koster et typisk udredningsforløb?',
      a: 'Et samlet ADHD-udredningsforløb koster 9.700 kr., en kombineret ADHD- og autismeudredning koster 13.200 kr., og ADHD-udredning inkl. første medicinsamtale koster 12.100 kr.'
    },
    {
      q: '2. Hvad er inkluderet i prisen?',
      a: 'Et typisk udredningsforløb indeholder klinisk indledende samtale, anamnese, strukturerede diagnosticeringsinterviews (f.eks. DIVA-5/AAA), tværfaglig konference, mundtlig tilbagemelding samt skriftlig epikrise og behandlingsplan.'
    },
    {
      q: '3. Kan den samlede pris ændre sig undervejs?',
      a: 'Prisen for det aftalte udredningsforløb ligger som udgangspunkt fast. Hvis der undervejs vurderes behov for supplerende undersøgelser eller konsultationer, aftales dette altid med dig på forhånd. Der tilføjes således ikke ekstra ydelser uden dit samtykke.'
    },
    {
      q: '4. Hvornår og hvordan betaler jeg?',
      a: 'Betaling afregnes efter hver konsultation.'
    },
    {
      q: '5. Kan jeg betale i rater?',
      a: 'Betaling sker efter hver enkelt konsultation. Udgiften fordeles derfor automatisk over forløbet, men vi tilbyder ikke en særskilt afdragsordning.'
    },
    {
      q: '6. Hvad gælder ved afbud eller udeblivelse?',
      a: 'Afbud skal meddeles senest 48 timer før den planlagte konsultation. Ved senere afbud eller udeblivelse opkræves fuldt honorar for sessionen.'
    },
    {
      q: '7. Kan jeg få tilskud fra Sygeforsikringen “danmark”?',
      a: 'Sygeforsikringen "danmark" yder i mange tilfælde tilskud til psykiatrisk og psykologisk udredning og behandling. Kontakt din afdeling hos "danmark" for specifikke tilskudssatser.'
    },
    {
      q: '8. Kan jeg få tilskud gennem en privat sundhedsforsikring?',
      a: 'Mange private sundhedsforsikringer dækker hele eller dele af et udrednings- og behandlingsforløb hos os. Undersøg dine vilkår hos dit forsikringsselskab.'
    },
    {
      q: '9. Kan jeg få tilskud som medlem af sygesikringsgruppe 2?',
      a: 'Hvis du er medlem af Sygeforsikring Danmark i gruppe 1, 2, 5 eller 6 og er 25 år eller yngre, kan du være berettiget til refusion for udredning og psykoterapi mod fremvisning af lægehenvisning. Sygeforsikring Danmark dækker ikke medicinsk behandling. Vi opfordrer dig til at kontakte Sygeforsikring Danmark direkte for at få bekræftet din specifikke dækning.'
    },
    {
      q: '10. Skal jeg have en henvisning fra egen læge?',
      a: 'Nej, da vi er en privat klinik, behøver du ingen henvisning fra din praktiserende læge. Du kan henvende dig direkte.'
    }
  ];

  const activeFaqList = (cmsFaq.length > 0 ? cmsFaq.map(f => ({
    q: getLocalizedValue(f.spoergsmaal),
    a: getLocalizedValue(f.svar)?.replace(/<[^>]*>?/gm, '') || ''
  })) : fallbackFaqItems).slice(0, 7);

  const heroEyebrow = getLocalizedValue(pageData?.hero_badge) || (lang === 'da' ? 'PRISER & YDELSER' : 'PRISER & TJÄNSTER');
  const heroTitle = getLocalizedValue(pageData?.hero_overskrift) || (lang === 'da' ? 'Priser på udredning og behandling' : 'Priser och tjänster');
  const heroDescRaw = getLocalizedValue(pageData?.hero_beskrivelse);
  const heroDesc = heroDescRaw ? heroDescRaw.replace(/<[^>]*>?/gm, '') : (lang === 'da'
    ? 'Se priser på udredning og behandling samt praktisk information om betaling, afbud og tilskud.'
    : 'Se priser för utredning och behandling samt praktisk information om betalning, avbokning och bidrag.');
  const heroCtaText = getLocalizedValue(pageData?.hero_cta_tekst) || (lang === 'da' ? 'Book en indledende samtale' : 'Kontakta oss om bokning');
  const heroCtaLink = pageData?.hero_cta_link || '/kontakt';
  const heroImgUrl = pageData?.hero_billede?.[0]
    ? (pageData.hero_billede[0].startsWith('https://assets.auxodev.dk') && !pageData.hero_billede[0].includes('/cdn-cgi/image/')
        ? pageData.hero_billede[0].replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/')
        : pageData.hero_billede[0])
    : (pageData ? null : "https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/hav.jpeg");

  return (
    <div className="bg-sand-light min-h-screen text-text-dark">
      {/* 1. HERO */}
      <FluidSubpageHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        description={heroDesc}
        ctaText={heroCtaText}
        ctaLink={heroCtaLink}
        bgImageUrl={heroImgUrl}
        bgImageAlt={lang === 'da' ? 'Priser og ydelser hos Nordisk Psykiatri' : 'Priser och tjänster hos Nordisk Psykiatri'}
        showBgOverlay={false}
        textContainerClass="-translate-y-10 sm:-translate-y-14 lg:-translate-y-20"
      />

      {/* 2. PRISOVERSIGT MED FANEBLADE */}
      <section className="py-16 lg:py-24 bg-sand-light">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-petrol-dark mb-6">
              {getLocalizedValue(pageData?.tabel_sektion_overskrift) || (lang === 'da' ? 'Priser' : 'Priser')}
            </h2>

            {/* Faneblads-knapper */}
            <div className="inline-flex p-1.5 rounded-full bg-sand-light border border-sand-medium/60 gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('udredning')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                  activeTab === 'udredning'
                    ? 'bg-petrol-dark text-white shadow-xs'
                    : 'text-[#566869] hover:text-petrol-dark'
                }`}
              >
                {lang === 'da' ? 'Udredning' : 'Assessment'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('behandling')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                  activeTab === 'behandling'
                    ? 'bg-petrol-dark text-white shadow-xs'
                    : 'text-[#566869] hover:text-petrol-dark'
                }`}
              >
                {lang === 'da' ? 'Behandling' : 'Treatment'}
              </button>
            </div>
          </div>

          {/* Tabelvisning */}
          <div className="bg-sand-light/20 border border-sand-medium/60 rounded-2xl p-6 sm:p-10 shadow-xs">
            <div className="hidden sm:grid sm:grid-cols-12 pb-3 mb-2 border-b border-sand-medium/80 text-xs font-semibold uppercase tracking-wider text-petrol-dark/70">
              <span className="sm:col-span-6 text-left">{lang === 'da' ? 'Ydelse' : 'Service'}</span>
              <span className="sm:col-span-3 text-center">{lang === 'da' ? 'Varighed' : 'Duration'}</span>
              <span className="sm:col-span-3 text-right">{lang === 'da' ? 'Pris' : 'Price'}</span>
            </div>

            {/* Begge lister bevares permanent i DOM'en for SEO/Googlebot og toggles visuelt */}
            <div className={activeTab === 'udredning' ? 'divide-y divide-sand-medium/60' : 'hidden'}>
              {activeUdredningList.map((item, idx) => (
                <div key={idx} className="py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                  <div className="sm:col-span-6 font-display text-lg sm:text-[19px] font-semibold text-petrol-dark leading-snug">
                    <span className="sm:hidden text-xs uppercase tracking-wider text-petrol-dark/60 block mb-0.5">{lang === 'da' ? 'Ydelse' : 'Service'}</span>
                    {item.name}
                  </div>
                  <div className="sm:col-span-3 text-xs sm:text-sm text-text-muted sm:text-center flex justify-between sm:block">
                    <span className="sm:hidden font-semibold text-petrol-dark/60">{lang === 'da' ? 'Varighed:' : 'Duration:'}</span>
                    <span>{item.duration}</span>
                  </div>
                  <div className="sm:col-span-3 font-semibold text-petrol-dark text-base sm:text-right flex justify-between sm:block">
                    <span className="sm:hidden text-xs uppercase tracking-wider text-petrol-dark/60">{lang === 'da' ? 'Pris:' : 'Price:'}</span>
                    <span>{item.price}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className={activeTab === 'behandling' ? 'divide-y divide-sand-medium/60' : 'hidden'}>
              {activeBehandlingList.map((item, idx) => (
                <div key={idx} className="py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                  <div className="sm:col-span-6 font-display text-lg sm:text-[19px] font-semibold text-petrol-dark leading-snug">
                    <span className="sm:hidden text-xs uppercase tracking-wider text-petrol-dark/60 block mb-0.5">{lang === 'da' ? 'Ydelse' : 'Service'}</span>
                    {item.name}
                  </div>
                  <div className="sm:col-span-3 text-xs sm:text-sm text-text-muted sm:text-center flex justify-between sm:block">
                    <span className="sm:hidden font-semibold text-petrol-dark/60">{lang === 'da' ? 'Varighed:' : 'Duration:'}</span>
                    <span>{item.duration}</span>
                  </div>
                  <div className="sm:col-span-3 font-semibold text-petrol-dark text-base sm:text-right flex justify-between sm:block">
                    <span className="sm:hidden text-xs uppercase tracking-wider text-petrol-dark/60">{lang === 'da' ? 'Pris:' : 'Price:'}</span>
                    <span>{item.price}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-4 border-t border-sand-medium/40 text-center">
              <a
                href="#faq"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-petrol-dark hover:text-petrol-medium"
              >
                <span>{lang === 'da' ? 'Se alle enkeltydelser og praktiske forbehold' : 'See all individual services and conditions'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. TYPISKE UDREDNINGSFORLØB (3 SAMLEDE PAKKER) */}
      <section className="py-16 lg:py-24 bg-[#D1E1BE] border-y border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-petrol-medium block mb-2">
              {getLocalizedValue(pageData?.pakker_sektion_badge) || (lang === 'da' ? 'SAMLEDE PAKKER' : 'COMPLETE PACKAGES')}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-petrol-dark">
              {getLocalizedValue(pageData?.pakker_sektion_overskrift) || (lang === 'da' ? 'Typiske udredningsforløb' : 'Typical assessment courses')}
            </h2>
            <div className="w-10 h-0.5 bg-petrol-medium/30 mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {activePackages.map((course, idx) => (
              <div key={idx} className="bg-sand-light/60 border border-sand-medium/60 rounded-2xl p-8 flex flex-col justify-between shadow-xs hover:border-petrol-medium/40 transition-colors">
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-petrol-dark text-center mb-3">
                    {course.title}
                  </h3>
                  
                  <div className="text-center mb-6">
                    <span className="font-display text-3xl font-bold text-petrol-dark">
                      {course.price}
                    </span>
                    <div className="w-12 h-0.5 bg-[#D1E1BE] mx-auto mt-3" />
                  </div>

                  <ul className="space-y-3 mb-8 text-xs sm:text-sm text-text-muted">
                    {course.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 rounded-full bg-petrol-dark text-white flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-2.5 h-2.5" />
                        </div>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to={course.buttonLink || '/kontakt'}
                  className="w-full text-center py-3.5 px-6 rounded-full bg-petrol-dark text-white font-semibold text-xs sm:text-sm hover:bg-petrol-medium transition-colors shadow-xs"
                >
                  {course.buttonText || (lang === 'da' ? 'Kontakt os om booking' : 'Kontakta oss om bokning')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PRAKTISK INFORMATION */}
      <section className="py-12 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="border border-sand-medium/60 rounded-2xl bg-sand-light/20 p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-sand-medium/60">
              {practicalPoints.map((pt, idx) => {
                const IconComp = pt.icon;
                return (
                  <div key={idx} className="p-4 sm:p-6 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-full bg-sand-light border border-sand-medium/60 flex items-center justify-center text-petrol-dark mb-3">
                      <IconComp className="w-5 h-5" />
                    </div>
                    <h4 className="font-display text-xl sm:text-2xl font-bold text-petrol-dark mb-1.5">
                      {pt.title}
                    </h4>
                    <p className="text-sm sm:text-[15px] text-text-muted">
                      {pt.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5. FAQ SEKTION */}
      <section id="faq" className="py-16 lg:py-24 bg-sand-light">
        <div className="mx-auto max-w-[900px] px-6">
          <div className="bg-[#faf8f4] rounded-lg p-6 sm:p-8 mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-petrol-dark mb-6">
              {getLocalizedValue(pageData?.faq_sektion_overskrift) || (lang === 'da' ? 'Ofte stillede spørgsmål om priser' : 'Vanliga frågor om priser')}
            </h2>

            <div className="space-y-3">
              {activeFaqList.map((item, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div 
                    key={idx} 
                    className={`border-t border-[#d8d5ce] last:border-b border-[#d8d5ce] transition-all duration-200 ${
                      isOpen ? 'py-3' : 'py-1'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      aria-expanded={isOpen}
                      className={`flex w-full items-center justify-between text-left font-sans text-sm font-bold text-petrol-dark focus:outline-none transition-all duration-200 cursor-pointer ${
                        isOpen ? 'py-2' : 'py-1.5'
                      }`}
                    >
                      <span>{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-petrol-medium shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-petrol-medium shrink-0" />
                      )}
                    </button>
                    {/* Bevares altid i DOM for Googlebot/crawlere, skjules/vises via class */}
                    <div className={`text-xs sm:text-sm text-[#566869] leading-relaxed transition-all duration-200 ${
                      isOpen ? 'mt-2 pb-2 block' : 'hidden'
                    }`}>
                      {item.a}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/vidensbank"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-petrol-dark hover:text-petrol-medium"
            >
              <span>{lang === 'da' ? 'Se alle spørgsmål i vidensbanken' : 'See all questions in the knowledge base'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. KONTAKTSEKTION */}
      <section className="w-full grid grid-cols-1 lg:grid-cols-12 bg-[#253a35] overflow-hidden">
        {/* Venstre tekstsektion */}
        <div className="lg:col-span-7 bg-[#253a35] text-white p-8 sm:p-12 lg:p-16 xl:p-20 flex flex-col justify-center items-start lg:items-end">
          <div className="w-full max-w-xl">
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4 text-white">
              {getLocalizedValue(pageData?.kontakt_overskrift) || (lang === 'da' ? 'Har du spørgsmål?' : 'Have questions?')}
            </h3>
            <p className="text-sm sm:text-base text-white/80 leading-relaxed mb-8">
              {getLocalizedValue(pageData?.kontakt_tekst) || (lang === 'da'
                ? 'Er du i tvivl om priser, tilskud eller andet praktisk? Du er altid velkommen til at kontakte os – vi hjælper gerne.'
                : 'In doubt about prices, subsidies, or practical matters? You are always welcome to contact us – we are happy to help.')}
            </p>
            <div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-petrol-dark font-semibold text-xs sm:text-sm rounded-full hover:bg-sand-light transition-colors"
              >
                <span>{lang === 'da' ? 'Kontakt os' : 'Contact us'}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Højre billede */}
        <div className="lg:col-span-5 relative min-h-[260px] lg:min-h-full">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/scandinavian-calm-water.jpg"
            alt="Roligt vand og klitter"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Schema.org FAQPage Structured Data for Google Rich Results */}
      {activeFaqList && activeFaqList.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": activeFaqList.map((item) => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": typeof item.a === 'string' ? item.a.replace(/<[^>]*>/g, '') : ''
                }
              }))
            })
          }}
        />
      )}
    </div>
  );
}
